import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { Button } from "../ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Plus, Trash2 } from "lucide-react";

const F = ({ label, children }) => (
  <label className="block space-y-1"><span className="text-xs font-semibold text-stone-600">{label}</span>{children}</label>
);

const TEXT = [["objective", "Objective *"], ["expected_results", "Expected results"], ["target_population", "Target community or population"],
  ["intervention", "Proposed program or intervention *"], ["resources", "Required resources"]];

export const PlanForm = ({ form, setForm, gkks, users, lockGkk }) => {
  const set = (k) => (e) => setForm({ ...form, [k]: e.target ? e.target.value : e });
  const ind = form.indicators || [];
  return (
    <div className="grid gap-3 sm:grid-cols-2">
      {TEXT.map(([k, l]) => <div key={k} className="sm:col-span-2"><F label={l}><Textarea rows={2} value={form[k] || ""} onChange={set(k)} data-testid={`plan-${k}-input`} /></F></div>)}
      <F label="Responsible GKK, ministry or parish office"><Input value={form.responsible_unit || ""} onChange={set("responsible_unit")} data-testid="plan-responsible-input" /></F>
      <F label="Implementation location"><Input value={form.location || ""} onChange={set("location")} data-testid="plan-location-input" /></F>
      <F label="GKK (leave as whole parish if parish-wide)">
        <Select value={form.gkk_id || "none"} disabled={lockGkk} onValueChange={(v) => setForm({ ...form, gkk_id: v === "none" ? null : v })}>
          <SelectTrigger className="bg-white" data-testid="plan-gkk-select"><SelectValue /></SelectTrigger>
          <SelectContent className="bg-white max-h-72"><SelectItem value="none">Whole parish</SelectItem>{gkks.map((g) => <SelectItem key={g.gkk_id} value={g.gkk_id}>{g.name}</SelectItem>)}</SelectContent>
        </Select>
      </F>
      <F label="Assigned coordinator">
        <Select value={form.coordinator_user_id || "none"} onValueChange={(v) => setForm({ ...form, coordinator_user_id: v === "none" ? null : v })}>
          <SelectTrigger className="bg-white" data-testid="plan-coordinator-select"><SelectValue /></SelectTrigger>
          <SelectContent className="bg-white max-h-72"><SelectItem value="none">Not yet assigned</SelectItem>{users.map((u) => <SelectItem key={u.user_id} value={u.user_id}>{u.name} · {u.role_label}</SelectItem>)}</SelectContent>
        </Select>
      </F>
      <F label="Start date"><Input type="date" value={form.start_date || ""} onChange={set("start_date")} data-testid="plan-start-input" /></F>
      <F label="Target completion date"><Input type="date" value={form.target_date || ""} onChange={set("target_date")} data-testid="plan-target-input" /></F>
      <F label="Proposed budget (₱)"><Input type="number" min="0" value={form.proposed_budget ?? ""} onChange={set("proposed_budget")} data-testid="plan-budget-input" /></F>
      <F label="Expected number of beneficiaries"><Input type="number" min="0" value={form.expected_beneficiaries ?? ""} onChange={set("expected_beneficiaries")} data-testid="plan-beneficiaries-input" /></F>
      <div className="sm:col-span-2 space-y-2">
        <p className="text-xs font-semibold text-stone-600">Performance indicators</p>
        {ind.map((x, i) => (
          <div key={i} className="flex gap-2">
            <Input placeholder="e.g. Consultation sessions held" value={x.name} onChange={(e) => setForm({ ...form, indicators: ind.map((y, j) => (j === i ? { ...y, name: e.target.value } : y)) })} data-testid={`indicator-name-${i}`} />
            <Input type="number" placeholder="Target" className="w-28" value={x.target ?? ""} onChange={(e) => setForm({ ...form, indicators: ind.map((y, j) => (j === i ? { ...y, target: e.target.value } : y)) })} data-testid={`indicator-target-${i}`} />
            <Button variant="ghost" size="icon" onClick={() => setForm({ ...form, indicators: ind.filter((_, j) => j !== i) })}><Trash2 className="h-4 w-4" /></Button>
          </div>
        ))}
        <Button variant="outline" size="sm" onClick={() => setForm({ ...form, indicators: [...ind, { name: "", target: "" }] })} data-testid="add-indicator-button"><Plus className="h-4 w-4 mr-1" />Add indicator</Button>
      </div>
    </div>
  );
};

const num = (v) => (v === "" || v === null || v === undefined ? null : Number(v));

export const planPayload = (f) => ({
  objective: f.objective, expected_results: f.expected_results || "", target_population: f.target_population || "",
  intervention: f.intervention, responsible_unit: f.responsible_unit || "", gkk_id: f.gkk_id || null,
  coordinator_user_id: f.coordinator_user_id || null, location: f.location || "",
  start_date: f.start_date || null, target_date: f.target_date || null, resources: f.resources || "",
  proposed_budget: num(f.proposed_budget), expected_beneficiaries: num(f.expected_beneficiaries),
  indicators: (f.indicators || []).filter((i) => i.name.trim()).map((i) => ({ name: i.name.trim(), target: num(i.target) })),
});

export const STATUS = {
  proposed: ["Proposed", "bg-stone-100 text-stone-700"], under_review: ["Under Review", "bg-amber-100 text-amber-900"],
  approved: ["Approved", "bg-sky-100 text-sky-900"], in_progress: ["In Progress", "bg-orange-100 text-orange-900"],
  completed: ["Completed", "bg-emerald-100 text-emerald-900"], evaluated: ["Evaluated", "bg-[#285E3B] text-white"],
  deferred: ["Deferred", "bg-stone-200 text-stone-600"], cancelled: ["Cancelled", "bg-red-100 text-red-800"],
  declined: ["Declined", "bg-red-100 text-red-800"],
};

export const StatusBadge = ({ s, testId }) => (
  <span className={`inline-block rounded-full px-2.5 py-0.5 text-xs font-semibold ${STATUS[s]?.[1]}`} data-testid={testId}>{STATUS[s]?.[0] || s}</span>
);
